export interface ApiErrorPayload {
  code: string
  message: string
  requestId?: string
  details?: unknown[]
}

export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly requestId: string | null
  readonly details: unknown[]

  constructor(status: number, payload: ApiErrorPayload) {
    super(payload.message)
    this.name = 'ApiError'
    this.status = status
    this.code = payload.code
    this.requestId = payload.requestId ?? null
    this.details = payload.details ?? []
  }
}

export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('Der Server ist gerade nicht erreichbar.')
    this.name = 'NetworkError'
    this.cause = cause
  }
}

let unauthorizedHandler: (() => void) | null = null

/** Wird in `main.ts` gesetzt: leitet bei 401 auf den Login um. */
export function setUnauthorizedHandler(handler: () => void): void {
  unauthorizedHandler = handler
}

const BASE = '/v1'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function toApiError(status: number, body: unknown): ApiError {
  if (isRecord(body) && isRecord(body.error)) {
    const error = body.error
    return new ApiError(status, {
      code: typeof error.code === 'string' ? error.code : 'UNKNOWN',
      message: typeof error.message === 'string' ? error.message : 'Unbekannter Fehler.',
      requestId: typeof error.requestId === 'string' ? error.requestId : undefined,
      details: Array.isArray(error.details) ? error.details : undefined,
    })
  }
  return new ApiError(status, { code: 'UNKNOWN', message: `Unerwartete Serverantwort (HTTP ${status}).` })
}

async function handle<T>(response: Response): Promise<T> {
  if (response.status === 401) {
    unauthorizedHandler?.()
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    if (!response.ok) throw toApiError(response.status, null)
    // 204 hat definitionsgemäß keinen Körper; Aufrufer typisieren dann `void`.
    return undefined as T
  }

  let body: unknown = null
  const contentType = response.headers.get('content-type') ?? ''
  if (contentType.includes('application/json')) {
    body = await response.json().catch(() => null)
  } else {
    const text = await response.text().catch(() => '')
    body = text.length > 0 ? { error: { code: 'UNKNOWN', message: text.slice(0, 500) } } : null
  }

  if (!response.ok) throw toApiError(response.status, body)
  return body as T
}

async function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  let response: Response
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      credentials: 'include',
      headers: body === undefined ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch (cause) {
    throw new NetworkError(cause)
  }
  return handle<T>(response)
}

export const http = {
  get: <T>(path: string): Promise<T> => send<T>('GET', path),
  post: <T>(path: string, body?: unknown): Promise<T> => send<T>('POST', path, body),
  patch: <T>(path: string, body: unknown): Promise<T> => send<T>('PATCH', path, body),
  delete: <T>(path: string, body?: unknown): Promise<T> => send<T>('DELETE', path, body),
}

/**
 * Multipart-Upload über XHR statt fetch — nur so lässt sich der Fortschritt
 * anzeigen, was bei Fotos über Mobilfunk spürbar hilft.
 */
export function uploadFile<T>(path: string, file: File, onProgress?: (percent: number) => void): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const form = new FormData()
    form.append('file', file, file.name)

    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${BASE}${path}`)
    xhr.withCredentials = true
    xhr.responseType = 'text'
    xhr.setRequestHeader('Accept', 'application/json')

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100))
      }
    })

    xhr.addEventListener('error', () => reject(new NetworkError(new Error('XHR error'))))
    xhr.addEventListener('abort', () => reject(new NetworkError(new Error('XHR abort'))))

    xhr.addEventListener('load', () => {
      if (xhr.status === 401) unauthorizedHandler?.()

      let parsed: unknown = null
      try {
        parsed = xhr.responseText.length > 0 ? JSON.parse(xhr.responseText) : null
      } catch {
        parsed = null
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(parsed as T)
      } else {
        reject(toApiError(xhr.status, parsed))
      }
    })

    xhr.send(form)
  })
}

/** Deutsche Fehlermeldung für beliebige Fehlerobjekte. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message
  if (error instanceof NetworkError) return error.message
  if (error instanceof Error) return error.message
  return 'Unbekannter Fehler.'
}
